'use client'

/**
 * The composer's footer row, its tools group and its icon button. They need
 * neither the Fude editor nor a menu, so a composer shell (the product
 * demo's box, its loading poster) renders them without either; the full
 * composer re-exports them from `prompt-input/primitives`.
 */
import { cn } from '@brew/ui/cn'
import { Children, type ComponentProps, type HTMLAttributes } from 'react'
import { InputGroupAddon, InputGroupButton } from '@/components/ui/input-group'

export type PromptInputFooterProps = Omit<
  ComponentProps<typeof InputGroupAddon>,
  'align'
>

export const PromptInputFooter = ({
  className,
  ...props
}: PromptInputFooterProps) => (
  <InputGroupAddon
    align="block-end"
    className={cn('justify-between gap-1 pb-2.5', className)}
    {...props}
  />
)

export type PromptInputToolsProps = HTMLAttributes<HTMLDivElement>

export const PromptInputTools = ({
  className,
  ...props
}: PromptInputToolsProps) => (
  <div className={cn('flex items-center gap-1', className)} {...props} />
)

export type PromptInputButtonProps = ComponentProps<typeof InputGroupButton>

export const PromptInputButton = ({
  variant = 'ghost',
  className,
  size,
  ...props
}: PromptInputButtonProps) => {
  const nextSize =
    size ?? (Children.count(props.children) > 1 ? 'sm' : 'icon-sm')

  return (
    <InputGroupButton
      className={cn(className)}
      size={nextSize}
      type="button"
      variant={variant}
      {...props}
    />
  )
}
